import './styles/statisticsDialog.css';
import { useGameStore } from '../../../store/gameStore';
import { useShallow } from 'zustand/shallow';
import Dialog from '../../subcomponents/Dialog';
import Toggle from '../../subcomponents/Toggle';
import Switch from '../../subcomponents/Switch';
import { useState } from 'react';
import Tile from '../../subcomponents/Tile';
import Chip from '../../subcomponents/Chip';
import Btn from '../../subcomponents/Btn';
import { FoundColor, GameMode, IAPColor } from '@/types/types';
import { colorUnlockables, hasStatSyncUnlocked } from '../../utils/unlockablesHelper';
import { useSyncStore } from '../../../store/syncStore';
import { generateSyncProfile, restoreStats } from '../../utils/syncHelper';
import { toast } from 'react-hot-toast';
import TextField from '../../subcomponents/TextField';
import CustomToast from '../../subcomponents/Toast';
import { Capacitor } from '@capacitor/core';
import UnlockDialog from './UnlockDialog';
import { PurchaseStatus, restorePurchases, buyStatSync } from '../../utils/iapHelper';

type StatisticsDialogProps = {
  open: boolean;
  onClose: () => void;
};

function StatisticsDialog(props: StatisticsDialogProps) {
  const { open, onClose } = props;
  const isWeb = Capacitor.getPlatform() === 'web';

  const {
    scoreStats,
    highScoreStats,
    stars,
    setFoundColor,
    foundColor,
    purchasesRestored,
    setPurchasesRestored,
  } = useGameStore(
    useShallow((state) => ({
      scoreStats: state.gameData,
      highScoreStats: state.gameStats,
      stars: state.stars,
      foundColor: state.foundColor,
      setFoundColor: state.setFoundColor,
      purchasesRestored: state.purchasesRestored,
      setPurchasesRestored: state.setPurchasesRestored,
    }))
  );

  const { syncCode } = useSyncStore();
  const [inputCode, setInputCode] = useState<string>('');
  const [loadingGenerate, setLoadingGenerate] = useState<boolean>(false);
  const [loadingRestore, setLoadingRestore] = useState<boolean>(false);
  const [displayMode, setSetDisplayMode] = useState<string>('Today');

  const toggleFoundColor = (color: FoundColor) => {
    setFoundColor(color);
  };

  const handleGenerate = async () => {
    if (loadingGenerate || loadingRestore) return;
    setLoadingGenerate(true);
    try {
      const state = useGameStore.getState();
      await generateSyncProfile({
        stars: state.stars,
        gameStats: state.gameStats,
        unlockedColors: state.unlockedColors,
      });
      toast.custom((t) => <CustomToast t={t} message="Sync code created" success />);
    } catch {
      toast.custom((t) => <CustomToast t={t} message="Failed to generate sync code" warning />);
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleRestore = async () => {
    if (loadingRestore || loadingGenerate) return;
    if (!inputCode.trim()) {
      toast.custom((t) => <CustomToast t={t} message="Enter a sync code" warning />);
      return;
    }
    if (inputCode.trim().toUpperCase() === syncCode) {
      toast.custom((t) => <CustomToast t={t} message="Sync code already applied" warning />);
      return;
    }
    setLoadingRestore(true);
    try {
      await restoreStats(inputCode.trim().toUpperCase());
      toast.custom((t) => <CustomToast t={t} message="Stats restored" success />);
      setInputCode('');
    } catch {
      toast.custom((t) => <CustomToast t={t} message="Sync code not found" warning />);
    } finally {
      setLoadingRestore(false);
    }
  };

  const handleRestorePurchases = async () => {
    if (purchasesRestored) {
      toast.custom((t) => <CustomToast t={t} message="Nothing to restore" warning />);
      return;
    }

    setIsLoading(true);
    const status: PurchaseStatus = await restorePurchases();
    setIsLoading(false);

    if (status === 'success') {
      toast.custom((t) => <CustomToast t={t} message="Purchases restored" star />);
    } else if (status === 'nothing_to_restore') {
      toast.custom((t) => <CustomToast t={t} message="Nothing to restore" warning />);
    } else if (status === 'error') {
      toast.custom((t) => <CustomToast t={t} message="Restore failed" warning />);
    }

    if (status === 'success' || status === 'nothing_to_restore') {
      setPurchasesRestored();
    }
  };

  const getStatsDisplay = (mode: GameMode) => {
    let words;
    let points;
    if (displayMode === 'Today') {
      words = scoreStats[mode].foundWords.length;
      points = scoreStats[mode].points;
    } else {
      words = highScoreStats[mode].wordsHighScore;
      points = highScoreStats[mode].pointsHighScore;
    }

    return (
      <>
        <b>{words}</b> {words === 1 ? 'word' : 'words'} / <b>{points}</b> pts.
      </>
    );
  };

  const modeDisplay: { title: string; mode: GameMode }[] = [
    { title: '4x4 BLITZ', mode: 'blitzSimple' },
    { title: '5x5 BLITZ', mode: 'blitzDeluxe' },
    { title: '4x4 ENDLESS', mode: 'endlessSimple' },
    { title: '5x5 ENDLESS', mode: 'endlessDeluxe' },
  ];

  // For IAP unlocks only
  const [showUnlockDialog, setShowUnlockDialog] = useState<boolean>(false);
  const [currentUnlockColor, setCurrentUnlockColor] = useState<FoundColor>('green');
  const [currentUnlockColorId, setCurrentUnlockColorId] = useState<IAPColor>('unlock_fire_color');
  const [currentPrice, setCurrentPrice] = useState<string>('');
  const [currentLetters, setCurrentLetters] = useState<string[]>([]);
  const handleUnlockDialog = (
    color: FoundColor,
    letters: string[],
    price?: string,
    unlockColorId?: IAPColor
  ) => {
    if (!price || !unlockColorId) return;
    setCurrentUnlockColor(color);
    setCurrentLetters(letters);
    setCurrentPrice(price);
    setCurrentUnlockColorId(unlockColorId);
    setShowUnlockDialog(true);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleStatSyncPurchase = async () => {
    setIsLoading(true);
    const status: PurchaseStatus = await buyStatSync();
    setIsLoading(false);

    if (status === 'success') {
      toast.custom((t) => <CustomToast t={t} message="Data transfer unlocked" star />);
    } else if (status === 'cancelled') {
      toast.custom((t) => <CustomToast t={t} message="Purchase cancelled" warning />);
    } else if (status === 'error') {
      toast.custom((t) => <CustomToast t={t} message="Purchase failed" warning />);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="STATISTICS">
      <div className="statistics-container">
        <div className="self-center mb-1">
          <Switch options={['Today', 'Best']} selected={displayMode} onChange={setSetDisplayMode} />
        </div>
        <div className="flex flex-col gap-1 items-center text-center">
          {modeDisplay.map(({ title, mode }, idx) => (
            <span key={idx} className="result-row">
              <span className="font-bold">{title}</span>
              <span className="stat-display">{getStatsDisplay(mode)}</span>
            </span>
          ))}
        </div>

        <hr className="statistics-divider" />
        <div className="statistics-subtitle">COLLECTED STARS</div>
        <div className="statistics-sibtitle-desc">
          Complete games to collect stars and unlock new found-word animation colors.
        </div>
        <div className="collected-stars-display">
          <span className="total-stars-text">Total Stars: </span>
          <Chip prependIcon="Star" starDisplay value={stars.toLocaleString()} />
        </div>

        {[...colorUnlockables]
          .sort((a, b) => {
            const isUnlockedA =
              a.unlockAmount !== undefined
                ? stars >= a.unlockAmount
                : (a.customUnlockCheck?.() ?? false);
            const isUnlockedB =
              b.unlockAmount !== undefined
                ? stars >= b.unlockAmount
                : (b.customUnlockCheck?.() ?? false);
            return isUnlockedA === isUnlockedB ? 0 : isUnlockedA ? -1 : 1;
          })
          .map(
            ({
              color,
              letters,
              unlockAmount,
              customUnlockCheck,
              chipIcon,
              chipText,
              unlockColorId,
              price,
            }) => {
              const isUnlocked =
                unlockAmount !== undefined
                  ? stars >= unlockAmount
                  : (customUnlockCheck?.() ?? false);

              return (
                <div key={color} className="unlock-row">
                  <div className="flex">
                    {letters.map((letter, idx) => {
                      const displayLetter =
                        isUnlocked || price || unlockColorId || chipText === ' Ko-fi'
                          ? letter
                          : '?';
                      if (color === 'rainbow') {
                        const rainbowColors: FoundColor[] = ['red', 'yellow', 'blue'];
                        const tileColor = rainbowColors[idx];
                        return (
                          <Tile
                            key={idx}
                            value={displayLetter}
                            small
                            noTransition
                            {...{ [tileColor]: foundColor === 'rainbow' }}
                          />
                        );
                      }
                      return (
                        <Tile
                          key={idx}
                          value={displayLetter}
                          small
                          noTransition
                          {...{ [color]: foundColor === color }}
                        />
                      );
                    })}
                  </div>
                  {isUnlocked ? (
                    <Toggle
                      selected={foundColor === color}
                      muteSoundWhenOff
                      setSelected={() => toggleFoundColor(color)}
                    />
                  ) : (
                    <>
                      {!unlockColorId && !price && (
                        <Chip
                          prependIcon={unlockAmount === undefined ? chipIcon : 'Star'}
                          starDisplay={unlockAmount !== undefined}
                          crownDisplay={unlockAmount === undefined && color === 'galaxy'}
                          dollarDisplay={unlockAmount === undefined}
                          value={unlockAmount?.toLocaleString() || (chipText ?? '')}
                        />
                      )}
                      {unlockColorId && price && (
                        <Btn
                          text={chipText ?? ''}
                          onClick={() => handleUnlockDialog(color, letters, price, unlockColorId)}
                          prependIcon="DollarSign"
                          yellowIcon
                          noFill
                          mini
                        />
                      )}
                    </>
                  )}
                  <UnlockDialog
                    open={showUnlockDialog}
                    onClose={() => {
                      setShowUnlockDialog(false);
                    }}
                    unlockColor={currentUnlockColor}
                    letters={currentLetters}
                    unlockColorId={currentUnlockColorId}
                    price={currentPrice}
                  />
                </div>
              );
            }
          )}

        <hr className="statistics-divider" />
        <div className="statistics-subtitle">DATA TRANSFER</div>
        <div className="statistics-sibtitle-desc">
          {hasStatSyncUnlocked() &&
            'Create a sync code to back up your progress, or enter one from another device to restore it.'}
          {!hasStatSyncUnlocked() &&
            isWeb &&
            'Support via Ko-fi to unlock the ability to transfer and back up your game statistics between devices.'}
          {!hasStatSyncUnlocked() &&
            !isWeb &&
            'Unlock the ability to transfer and back up your game statistics between devices with a one-time purchase of $4.99.'}
        </div>
        {!hasStatSyncUnlocked() && !isWeb && (
          <div className="flex justify-center mt-1">
            <Btn text="PURCHASE" onClick={handleStatSyncPurchase} loading={isLoading} />
          </div>
        )}
        {hasStatSyncUnlocked() && (
          <>
            {}
            {syncCode ? (
              <div className="flex flex-col items-center gap-2">
                <div className="data-sync-text">Your code:</div>
                <div className="flex">
                  <Chip value={syncCode} isMasked />
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <Btn text="CREATE CODE" onClick={handleGenerate} loading={loadingGenerate} />
              </div>
            )}
            <div className="mt-4 flex flex-col items-center gap-3">
              <div className="data-sync-text -mb-1">Restore from code:</div>
              <TextField
                value={inputCode}
                onChange={setInputCode}
                placeholder="[ e.g. ABCD-1234 ]"
              />
              <Btn text="RESTORE" onClick={handleRestore} loading={loadingRestore} />
            </div>
          </>
        )}
        {!isWeb && (
          <>
            <hr className="statistics-divider" />
            <div className="statistics-subtitle">RESTORE PURCHASES</div>
            <div className="statistics-sibtitle-desc">Restore previously purchased content.</div>
            <div className="flex justify-center mt-1">
              <Btn text="RESTORE" onClick={handleRestorePurchases} loading={isLoading} />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

export default StatisticsDialog;
